import { useCallback, useMemo, useState } from 'react';
import type {
  RTPDownloadResult,
  RTPPreviewInputSnapshot,
  RTPPreviewState,
  RTPSideResults,
} from '@/types/rtpDownload';

interface UseRTPPreviewDownloadOptions {
  timeRange: { from: string | null; to: string | null };
  grouping: RTPPreviewInputSnapshot['grouping'];
  patientIds: string[];
  fetchResults: () => Promise<Map<string, RTPDownloadResult>>;
}

interface UseRTPPreviewDownloadResult {
  cachedResults: Map<string, RTPDownloadResult> | null;
  previewData: Map<string, RTPSideResults> | null;
  previewState: RTPPreviewState;
  selectedPreviewPatientIds: Set<string>;
  setSelectedPreviewPatientIds: (ids: Set<string>) => void;
  loadPreview: () => Promise<{ count: number }>;
  isPreviewStale: () => boolean;
}

export function useRTPPreviewDownload({
  timeRange,
  grouping,
  patientIds,
  fetchResults,
}: UseRTPPreviewDownloadOptions): UseRTPPreviewDownloadResult {
  const [cachedResults, setCachedResults] = useState<Map<string, RTPDownloadResult> | null>(
    null
  );
  const [previewState, setPreviewState] = useState<RTPPreviewState>({
    isLoading: false,
    error: null,
  });
  const [selectedPreviewPatientIds, setSelectedPreviewPatientIds] = useState<Set<string>>(
    new Set()
  );
  const [previewInputSnapshot, setPreviewInputSnapshot] =
    useState<RTPPreviewInputSnapshot | null>(null);

  const previewData: Map<string, RTPSideResults> | null = useMemo(() => {
    if (!cachedResults) return null;
    return new Map(
      Array.from(cachedResults.entries()).map(([id, result]) => [
        id,
        {
          left: result.leftFoot,
          right: result.rightFoot,
        },
      ])
    );
  }, [cachedResults]);

  const isPreviewStale = useCallback((): boolean => {
    if (!cachedResults || !previewInputSnapshot) return true;

    return (
      !timeRange.from ||
      !timeRange.to ||
      timeRange.from !== previewInputSnapshot.timeRange.from ||
      timeRange.to !== previewInputSnapshot.timeRange.to ||
      grouping !== previewInputSnapshot.grouping ||
      JSON.stringify([...patientIds].sort()) !==
        JSON.stringify([...previewInputSnapshot.patientIds].sort())
    );
  }, [cachedResults, previewInputSnapshot, timeRange.from, timeRange.to, grouping, patientIds]);

  const loadPreview = useCallback(async () => {
    setPreviewState({ isLoading: true, error: null });

    try {
      const resultsMap = await fetchResults();
      const filteredMap = new Map(
        Array.from(resultsMap.entries()).filter(
          ([_, result]) => result.leftFoot.length > 0 || result.rightFoot.length > 0
        )
      );

      setCachedResults(filteredMap);
      setSelectedPreviewPatientIds(new Set(filteredMap.keys()));
      setPreviewInputSnapshot({
        patientIds: [...patientIds],
        timeRange: { from: timeRange.from!, to: timeRange.to! },
        grouping,
      });
      setPreviewState({ isLoading: false, error: null });

      return { count: filteredMap.size };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Preview failed';
      setPreviewState({ isLoading: false, error: message });
      throw error;
    }
  }, [fetchResults, patientIds, timeRange.from, timeRange.to, grouping]);

  return {
    cachedResults,
    previewData,
    previewState,
    selectedPreviewPatientIds,
    setSelectedPreviewPatientIds,
    loadPreview,
    isPreviewStale,
  };
}
